import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { defaultCheck } from "../actions/Checks/constants";
import { formatCheck2Frontend } from "../actions/Checks/util";
import { defaultLayer } from "../actions/Layers/constants";
import { format2Frontend } from "../actions/Layers/util";
import useConfig from "../actions/Tenants/config/configHook";
import { useConnectivity } from "../offline/connectivityMonitor";
import { isOfflineChecksEnabled } from "../offline/db";
import { lookupById as lookupLayerById } from "../offline/layerCache";
import { callAvosApi } from "../utils/useAvosApiHook";

export function useOfflineMode() {
    const config = useConfig();
    const { isOnline } = useConnectivity();
    return isOfflineChecksEnabled(config) && !isOnline;
}

export function useCurrentLayer(tenantId: string | undefined, layerId: string | undefined, enabled = true) {
    const inOfflineMode = useOfflineMode();

    return useQuery({
        // "always" so the offline branch below still runs while navigator.onLine is false.
        networkMode: "always",
        queryKey: ["layer", tenantId, layerId],
        queryFn: async () => {
            if (inOfflineMode) {
                const cached = await lookupLayerById(layerId as string);
                return cached ? format2Frontend(cached) : null;
            }
            const response = await callAvosApi(`/${tenantId}/layers/${layerId}`);
            return format2Frontend(response.data);
        },
        enabled: enabled && !!tenantId && !!layerId,
    });
}

export function useCurrentCheck(tenantId: string | undefined, checkId: number | undefined, enabled = true) {
    const inOfflineMode = useOfflineMode();

    return useQuery({
        queryKey: ["check", tenantId, checkId],
        queryFn: async () => {
            const response = await callAvosApi(`/${tenantId}/checks/${checkId}/`);
            return formatCheck2Frontend(response.data);
        },
        // Offline checks are synthetic and only live in the cache, so never refetch them.
        enabled: enabled && !inOfflineMode && !!tenantId && checkId !== undefined && Number.isFinite(checkId),
    });
}

export function useLayerLocationHistory(tenantId: string | undefined, layerId: string | null | undefined, enabled = true) {
    // Never fetched offline: TanStack can still think it's online on a dead network
    // (navigator.onLine stuck on true), and a retrying fetch keeps the start-check buttons disabled.
    const inOfflineMode = useOfflineMode();

    return useQuery({
        queryKey: ["layerLocationHistory", tenantId, layerId],
        queryFn: async () => {
            const response = await callAvosApi(`/${tenantId}/layers/${layerId}/locations`);
            return response.data;
        },
        enabled: enabled && !inOfflineMode && !!tenantId && !!layerId,
    });
}

// Route-scoped layer for components rendered under /:tenant_id/layer/:layer_id.
// TanStack dedupes the request, so every consumer shares one fetch.
export function useRouteLayer() {
    const params = useParams();
    const { data } = useCurrentLayer(params.tenant_id, params.layer_id);
    return (data ?? defaultLayer) as any;
}

export function useRouteLayerQuery() {
    const params = useParams();
    return useCurrentLayer(params.tenant_id, params.layer_id);
}

// Route-scoped check for components rendered under an .../add-check/:check_id route.
export function useRouteCheckQuery() {
    const params = useParams();
    const checkId = parseFloat(params.check_id as string);
    return useCurrentCheck(params.tenant_id, Number.isNaN(checkId) ? undefined : checkId);
}

export function useRouteCheck() {
    const { data } = useRouteCheckQuery();
    return (data ?? defaultCheck) as any;
}

export function useLayerChildren(tenantId: string | undefined, layerId: string | undefined, params: any = { children_level: 2, limit: 100 }) {
    return useQuery({
        queryKey: ["layerChildren", tenantId, layerId, JSON.stringify(params)],
        queryFn: async () => {
            const response = await callAvosApi(`/${tenantId}/layers/${layerId}/children`, { params });
            const data = response.data ?? {};
            return { ...data, results: (data.results ?? []).map(format2Frontend) };
        },
        enabled: !!tenantId && !!layerId,
    });
}

export function useRouteLayerChildren() {
    const params = useParams();
    const { data } = useLayerChildren(params.tenant_id, params.layer_id);
    return data ?? false;
}

export function useRouteLayerChildrenQuery() {
    const params = useParams();
    return useLayerChildren(params.tenant_id, params.layer_id);
}
